import { dividerClasses, type DividerVariantProps } from "./Divider.style";

import { Ref, useCallback, useMemo } from "react";

import {
  SeparatorProps as AriaSeparatorProps,
  useSeparator,
} from "./use-separator";

import { HTMLSpocUIProps, PropGetter } from "@/types";

interface Props extends HTMLSpocUIProps<"hr"> {
  /**
   * Ref to the DOM node.
   */
  ref?: Ref<HTMLElement> | undefined;
}

export type UseDividerProps = Props &
  DividerVariantProps &
  Omit<AriaSeparatorProps, "elementType">;

export function useDivider(props: UseDividerProps) {
  const { component, className, orientation, ...otherProps } = props;

  let Component = component || "hr";

  if (Component === "hr" && orientation === "vertical") {
    Component = "span";
  }

  const { separatorProps } = useSeparator({
    elementType: typeof Component === "string" ? Component : "hr",
    orientation,
  });

  const styles = useMemo(
    () =>
      dividerClasses({
        orientation,
        className,
      }),
    [orientation, className]
  );

  const getDividerProps: PropGetter = useCallback(
    (props = {}) => ({
      className: styles,
      role: "separator",
      "data-orientation": orientation,
      ...separatorProps,
      ...otherProps,
      ...props,
    }),
    [styles, orientation, separatorProps, otherProps]
  );

  return { Component, getDividerProps };
}

export type UseDividerReturn = ReturnType<typeof useDivider>;
